import { z } from "zod";
import {
  A2pBusinessProfileSchema,
  A2pStatusSchema,
  A2pSubmitBrandSchema,
  A2pSubmitCampaignSchema,
  CreateTelephonyAccountSchema,
  TelephonyAccountListSchema,
  TelephonyAccountSchema,
  TelephonyAvailableNumberListSchema,
  TelephonyPhoneNumberListSchema,
  TelephonyPhoneNumberSchema,
  TelephonyPurchaseNumberSchema,
  TelephonySearchNumbersSchema,
  TelephonyUpdateNumberSchema,
  TelephonyUsageSummarySchema,
  UpdateTelephonyAccountSchema,
  type A2pBusinessProfileDto,
  type A2pStatus,
  type A2pSubmitBrandDto,
  type A2pSubmitCampaignDto,
  type CreateTelephonyAccountDto,
  type TelephonyAccount,
  type TelephonyAvailableNumber,
  type TelephonyPhoneNumber,
  type TelephonyPurchaseNumberDto,
  type TelephonySearchNumbersDto,
  type TelephonyUpdateNumberDto,
  type TelephonyUsageSummary,
  type UpdateTelephonyAccountDto,
} from "@lumika/shared";
import { apiFetch } from "./client";

/**
 * Dealership-portal Telephony API client. All routes are scoped to
 * the authenticated user's dealership via the runtime backend's
 * AppAuthGuard — no dealership ID in the URL.
 */
export const telephonyApi = {
  // Telephony Accounts
  listAccounts: (): Promise<TelephonyAccount[]> =>
    apiFetch(`/dealerships/me/telephony-accounts`, TelephonyAccountListSchema),
  createAccount: (input: CreateTelephonyAccountDto): Promise<TelephonyAccount> => {
    const body = CreateTelephonyAccountSchema.parse(input);
    return apiFetch(`/dealerships/me/telephony-accounts`, TelephonyAccountSchema, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  getAccount: (id: string): Promise<TelephonyAccount> =>
    apiFetch(`/telephony-accounts/${id}`, TelephonyAccountSchema),
  updateAccount: (id: string, input: UpdateTelephonyAccountDto): Promise<TelephonyAccount> => {
    const body = UpdateTelephonyAccountSchema.parse(input);
    return apiFetch(`/telephony-accounts/${id}`, TelephonyAccountSchema, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
  removeAccount: (id: string): Promise<void> =>
    apiFetch(`/telephony-accounts/${id}`, TelephonyAccountSchema.optional(), {
      method: "DELETE",
    }) as Promise<void>,

  // Numbers
  searchNumbers: (
    accountId: string,
    input: TelephonySearchNumbersDto,
  ): Promise<TelephonyAvailableNumber[]> => {
    const body = TelephonySearchNumbersSchema.parse(input);
    return apiFetch(
      `/telephony-accounts/${accountId}/phone-numbers/search`,
      TelephonyAvailableNumberListSchema,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  purchaseNumber: (
    accountId: string,
    input: TelephonyPurchaseNumberDto,
  ): Promise<TelephonyPhoneNumber> => {
    const body = TelephonyPurchaseNumberSchema.parse(input);
    return apiFetch(
      `/telephony-accounts/${accountId}/phone-numbers`,
      TelephonyPhoneNumberSchema,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  listAccountNumbers: (accountId: string): Promise<TelephonyPhoneNumber[]> =>
    apiFetch(
      `/telephony-accounts/${accountId}/phone-numbers`,
      TelephonyPhoneNumberListSchema,
    ),
  listMyNumbers: (): Promise<TelephonyPhoneNumber[]> =>
    apiFetch(`/dealerships/me/phone-numbers`, TelephonyPhoneNumberListSchema),
  updateNumber: (
    id: string,
    input: TelephonyUpdateNumberDto,
  ): Promise<TelephonyPhoneNumber> => {
    const body = TelephonyUpdateNumberSchema.parse(input);
    return apiFetch(`/telephony-phone-numbers/${id}`, TelephonyPhoneNumberSchema, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
  releaseNumber: (id: string): Promise<void> =>
    apiFetch(`/telephony-phone-numbers/${id}`, TelephonyPhoneNumberSchema.optional(), {
      method: "DELETE",
    }) as Promise<void>,
  smsEnable: (id: string): Promise<TelephonyPhoneNumber> =>
    apiFetch(`/telephony-phone-numbers/${id}/sms-enable`, TelephonyPhoneNumberSchema, {
      method: "POST",
    }),
  smsDisable: (id: string): Promise<TelephonyPhoneNumber> =>
    apiFetch(`/telephony-phone-numbers/${id}/sms-disable`, TelephonyPhoneNumberSchema, {
      method: "POST",
    }),

  // A2P
  saveBusinessProfile: (id: string, input: A2pBusinessProfileDto): Promise<TelephonyAccount> => {
    const body = A2pBusinessProfileSchema.parse(input);
    return apiFetch(
      `/telephony-accounts/${id}/business-profile`,
      TelephonyAccountSchema,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  submitBrand: (id: string, input: A2pSubmitBrandDto): Promise<TelephonyAccount> => {
    const body = A2pSubmitBrandSchema.parse(input);
    return apiFetch(`/telephony-accounts/${id}/brand`, TelephonyAccountSchema, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  resendOtp: (id: string): Promise<TelephonyAccount> =>
    apiFetch(`/telephony-accounts/${id}/brand/otp/resend`, TelephonyAccountSchema, {
      method: "POST",
    }),
  refreshBrand: (id: string): Promise<TelephonyAccount> =>
    apiFetch(`/telephony-accounts/${id}/brand/refresh`, TelephonyAccountSchema, {
      method: "POST",
    }),
  submitCampaign: (
    id: string,
    input: A2pSubmitCampaignDto,
  ): Promise<{ id: string; sid: string | null; status: string }> => {
    const body = A2pSubmitCampaignSchema.parse(input);
    return apiFetch(
      `/telephony-accounts/${id}/campaigns`,
      z.object({ id: z.string().uuid(), sid: z.string().nullable(), status: z.string() }),
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  a2pStatus: (id: string): Promise<A2pStatus> =>
    apiFetch(`/telephony-accounts/${id}/a2p-status`, A2pStatusSchema),

  // Usage
  accountUsage: (
    id: string,
    range: { start: string; end: string },
  ): Promise<TelephonyUsageSummary> => {
    const params = new URLSearchParams(range);
    return apiFetch(
      `/telephony-accounts/${id}/usage?${params}`,
      TelephonyUsageSummarySchema,
    );
  },
  myUsage: (range: { start: string; end: string }): Promise<TelephonyUsageSummary> => {
    const params = new URLSearchParams(range);
    return apiFetch(`/dealerships/me/usage?${params}`, TelephonyUsageSummarySchema);
  },
  syncUsage: (
    id: string,
    range: { start: string; end: string },
  ): Promise<{ rowsUpserted: number }> => {
    const params = new URLSearchParams(range);
    return apiFetch(
      `/telephony-accounts/${id}/usage/sync?${params}`,
      z.object({ rowsUpserted: z.number() }),
      { method: "POST" },
    );
  },

  // Voice SDK access token (TELEPHONY_PER_ACCOUNT.md §"Access Token mint").
  // `callerPhoneNumberE164` is the server-resolved caller-ID for the
  // rooftop — pass it as the explicit From so shared accounts never
  // fall back to another dealership's number.
  mintAccessToken: (opts?: {
    rooftopId?: string;
    telephonyAccountId?: string;
  }): Promise<{
    token: string;
    identity: string;
    expiresAt: string;
    callerPhoneNumberE164: string | null;
  }> => {
    const body: Record<string, unknown> = {};
    if (opts?.rooftopId) body.rooftopId = opts.rooftopId;
    if (opts?.telephonyAccountId) body.telephonyAccountId = opts.telephonyAccountId;
    return apiFetch(
      `/voice/access_token`,
      z.object({
        token: z.string(),
        identity: z.string(),
        expiresAt: z.string(),
        callerPhoneNumberE164: z.string().nullable(),
      }),
      { method: "POST", body: JSON.stringify(body) },
    );
  },

  /**
   * Pre-create an `activities` row + voice lead so the
   * softphone dial has a Lumika id to thread the Twilio webhook +
   * status callbacks against. Mode `operator` = the agent picks up
   * the Voice SDK and talks; `ai` = the AI Call orchestrator places
   * the call server-side. Returns the activity id to stamp into
   * Twilio's `Device.connect({ params })`.
   */
  prepareOutboundCall: (input: {
    contactId: string;
    to: string;
    mode: "operator" | "ai";
    /** The location to call FROM; omit to let the server pick. */
    rooftopId?: string;
    /** Optional operator intent for the AI agent (mode='ai' only, ≤500 chars). */
    notes?: string;
  }): Promise<{ activityId: string; leadId: string }> =>
    apiFetch(
      `/voice/outbound-call`,
      z.object({ activityId: z.string(), leadId: z.string() }),
      { method: "POST", body: JSON.stringify(input) },
    ),

  /**
   * The locations this user can place calls FROM. Empty = hide the
   * Call/AI-Call buttons; more than one = offer a location picker.
   */
  voiceCapabilities: (): Promise<{
    rooftops: Array<{ id: string; name: string }>;
  }> =>
    apiFetch(
      `/voice/capabilities`,
      z.object({
        rooftops: z.array(z.object({ id: z.string(), name: z.string() })),
      }),
    ),
};
